import React, { useState } from "react";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Box, Button, Grid, Stack, styled, Typography, useMediaQuery, useTheme } from "@mui/material";
import { ReactComponent as Bitcoiner } from "assets/images/bitcoiner/bitcoiner.svg";
import PageTitle from "components/elements/PageTitle";
import Panel from "components/elements/Panel";
import { ChallengeStatusBadge } from "components/modules/ChallengeStatusCard";
import { fontWeight } from "config/Theme/options";
import { useMUIThemeModeContext } from "context/MUIThemeModeContext";
import { useUserDataContext } from "context/UserDataContext";
import { useAPIBitcoinerChallengeDetail } from "hooks/useAPI";
import useResponsiveFont from "hooks/useResponsiveFont";
import { Trans, useTranslation } from "react-i18next";
import { WeeklyQuizStatus } from "types/enums";

import Atlas from "./Atlas";
import QuizModal from "./QuizModal";
import Skeleton from "./Skeleton";
import StreakBlock from "./StreakBlock";
import Table from "./Table";
import UnfreezeQuizBox from "./UnfreezeQuizBox";
import XPProgress from "./XPProgress";

const Wrapper = styled(Box)(({ theme }) => {
  const { colors } = useMUIThemeModeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return {
    backgroundColor: colors.iceBlue,
    padding: isMobile ? theme.spacing(1.5) : theme.spacing(3),
    borderRadius: "16px",
  };
});

const FROZEN_STATUSES = [WeeklyQuizStatus.FROZEN_1, WeeklyQuizStatus.FROZEN_2, WeeklyQuizStatus.FROZEN_3];
const START_STATUSES = [WeeklyQuizStatus.WAITING, WeeklyQuizStatus.OPEN];

const BitcoinerChallenge: React.FC = () => {
  const { t } = useTranslation();
  const { colors } = useMUIThemeModeContext();
  const theme = useTheme();
  const { getResponsiveRem } = useResponsiveFont();
  const { data, mutate } = useAPIBitcoinerChallengeDetail();
  const { refreshData } = useUserDataContext();

  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  if (!data) {
    return <Skeleton />;
  }

  const isStartAllowed = START_STATUSES.includes(data.overview.weeklyQuizStatus);
  const isFrozen = FROZEN_STATUSES.includes(data.overview.weeklyQuizStatus);
  const isOpen = data.weeklyQuiz.status === WeeklyQuizStatus.OPEN;
  const isDone = data.weeklyQuiz.status === WeeklyQuizStatus.DONE;
  const streakCount = data.weeklyQuiz.streakCurrentAnswers;

  const handleCloseModal = () => {
    setModalOpen(false);
    mutate();
    refreshData?.();
  };

  return (
    <Panel sx={{ overflow: "hidden", p: { xs: 2, sm: 4 } }}>
      <PageTitle i18nKey="bitcoinerChallenge.meta_title" />
      <Grid container spacing={{ xs: 1, sm: 3 }}>
        <Grid item xs={12} lg={6}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={"space-between"}
            px={{ xs: 1, sm: 3 }}
            spacing={{ xs: 1, sm: 2 }}>
            <Stack
              spacing={1.5}
              textAlign={isMobile ? "center" : "left"}
              alignItems={isMobile ? "center" : "flex-start"}>
              {isMobile && (
                <Box
                  sx={{
                    width: 96,
                    height: 96,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    my: 3,
                  }}>
                  <Bitcoiner style={{ width: "100%", height: "auto" }} />
                </Box>
              )}
              <Typography fontSize={"2rem"} fontWeight={fontWeight.extraBold} fontFamily={"Nunito"}>
                {t("bitcoinerChallenge.title")}
              </Typography>
              <Typography
                fontSize={"1rem"}
                fontWeight={fontWeight.bold}
                fontFamily={"Nunito"}
                lineHeight={"140%"}
                color={"primary.main"}>
                {t("bitcoinerChallenge.description")}
              </Typography>
            </Stack>
            {!isMobile && (
              <Box sx={{ width: 138, height: 138, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Bitcoiner style={{ width: "100%", height: "auto" }} />
              </Box>
            )}
          </Stack>
        </Grid>
        <Grid item xs={12} lg={6} display="flex" alignItems="center">
          <XPProgress overview={data.overview} />
        </Grid>
      </Grid>
      <Grid container spacing={3} pt={isMdDown ? 3 : 2}>
        <Grid item xs={12} lg={6}>
          <Stack spacing={2}>
            <Wrapper>
              <Stack spacing={2}>
                <Stack
                  justifyContent={"space-between"}
                  spacing={{ xs: 1, md: 2 }}
                  direction={{ xs: "column", md: "row" }}
                  alignItems={"center"}>
                  <Typography fontSize={"1.375rem"} fontWeight={fontWeight.extraBold} fontFamily={"Nunito"}>
                    {t("bitcoinerChallenge.weeklyQuiz.title", { number: data.weeklyQuiz.week })}
                  </Typography>
                  <ChallengeStatusBadge status={data.weeklyQuiz.status} width={isMdDown ? "100%" : undefined} />
                </Stack>
                <Stack
                  justifyContent={"space-between"}
                  spacing={2}
                  direction={{ xs: "column", md: "row" }}
                  alignItems={{ xs: "flex-start", md: "center" }}>
                  <StreakBlock streakCount={streakCount} />
                  {isDone ? (
                    <Stack spacing={1} alignItems={"center"} direction={"row"}>
                      <AccessTimeIcon color="primary" />
                      <Typography color="primary" fontSize={"1rem"} fontWeight={fontWeight.bold} fontFamily={"Nunito"}>
                        {t("bitcoinerChallenge.weeklyQuiz.next")}
                      </Typography>
                    </Stack>
                  ) : (
                    <Button
                      size={isMdDown ? "medium" : "large"}
                      sx={{ px: 2 }}
                      variant="contained"
                      fullWidth={isMdDown}
                      disabled={!isStartAllowed}
                      onClick={() => setModalOpen(true)}>
                      {t("bitcoinerChallenge.weeklyQuiz.cta")}
                    </Button>
                  )}
                </Stack>
              </Stack>
              {isFrozen && (
                <UnfreezeQuizBox
                  status={data.weeklyQuiz.status}
                  mutateData={() => mutate()}
                  unfreezeCreditsCosts={data.weeklyQuiz.unfreezeCreditsCosts}
                />
              )}
              {isOpen && (
                <Typography fontSize={getResponsiveRem(0.85)} pt={2.5} fontFamily={"Nunito"}>
                  <Trans i18nKey={"bitcoinerChallenge.weeklyQuiz.openInfo"} /> ⌛
                </Typography>
              )}
            </Wrapper>
            <Wrapper>
              <Stack spacing={2}>
                <Typography
                  fontSize={"1.375rem"}
                  textAlign={isMdDown ? "center" : "left"}
                  fontWeight={fontWeight.extraBold}
                  fontFamily={"Nunito"}>
                  {t("bitcoinerChallenge.random.title")}
                </Typography>
                <Stack
                  justifyContent={"space-between"}
                  spacing={2}
                  direction={{ xs: "column", md: "row" }}
                  alignItems={{ xs: "flex-start", md: "center" }}>
                  <Box>
                    <Typography
                      fontSize={getResponsiveRem(0.875)}
                      fontWeight={fontWeight.extraBold}
                      fontFamily={"Nunito"}>
                      {t("bitcoinerChallenge.random.subtitle")}
                    </Typography>
                    <Typography
                      fontSize={getResponsiveRem(0.8125)}
                      fontWeight={fontWeight.semiBold}
                      fontFamily={"Nunito"}
                      color={colors.gray500}>
                      {t("bitcoinerChallenge.random.caption")}
                    </Typography>
                  </Box>
                  <Button
                    size={isMdDown ? "medium" : "large"}
                    sx={{ px: 2 }}
                    variant="contained"
                    fullWidth={isMdDown}
                    component="a"
                    href={"/kviz"}>
                    {t("bitcoinerChallenge.random.cta")}
                  </Button>
                </Stack>
              </Stack>
            </Wrapper>
            <Wrapper>
              <Typography fontSize={getResponsiveRem(1)} fontWeight={fontWeight.extraBold} fontFamily={"Nunito"}>
                {t("bitcoinerChallenge.rewards.title")}
              </Typography>
              <Table levels={data.allLevels} levelNumber={data.overview.levelNumber} />
            </Wrapper>
          </Stack>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Wrapper height={"100%"}>
            <Atlas />
          </Wrapper>
        </Grid>
      </Grid>
      {modalOpen && <QuizModal weeklyQuiz={data.weeklyQuiz} onClose={handleCloseModal} />}
    </Panel>
  );
};

export default BitcoinerChallenge;
